import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_SIZES = ["1", "2", "3", "4", "5", "6", "7", "8", "10", "12+"];

export default function ReservationForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    date: "",
    time: "",
    party_size: "2",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.time) {
      toast.error("Please fill in name, phone, date and time.");
      return;
    }
    setLoading(true);
    try {
      const partyNum = parseInt(form.party_size.replace("+", ""), 10) || 2;
      await axios.post(`${API}/reservations`, {
        ...form,
        party_size: partyNum,
      });
      setDone(true);
      toast.success("Reservation request received! We'll call you to confirm.");
      setForm({
        name: "",
        phone: "",
        date: "",
        time: "",
        party_size: "2",
        notes: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Couldn't submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section
      id="reserve"
      data-testid="reservation-section"
      className="py-20 sm:py-28 bg-[#F9F6F0]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 reveal">
          <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
            Reserve
          </p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Save your table at Meritz.
          </h2>
          <p className="mt-4 text-[#2C2825]/75 leading-relaxed">
            Tell us when you&rsquo;re coming and we&rsquo;ll have it ready. For
            same-day bookings within 2 hours, please give us a quick call.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-[#2C2825]/80">
            <li>· Free for parties of 1–8</li>
            <li>· Group bookings of 9+ welcomed — leave a note below</li>
            <li>· Confirmation via WhatsApp / phone within the hour</li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="reservation-form"
          className="lg:col-span-7 bg-white border border-[#E8E2D6] rounded-3xl p-6 sm:p-8 shadow-sm reveal"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name">
              <input
                data-testid="reservation-name"
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Chioma O."
                className={inputClass}
                required
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid="reservation-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+234 ..."
                className={inputClass}
                required
              />
            </Field>
            <Field label="Date">
              <input
                data-testid="reservation-date"
                type="date"
                min={today}
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                className={inputClass}
                required
              />
            </Field>
            <Field label="Time">
              <input
                data-testid="reservation-time"
                type="time"
                value={form.time}
                onChange={(e) => update("time", e.target.value)}
                className={inputClass}
                required
              />
            </Field>
            <Field label="Party size">
              <Select
                value={form.party_size}
                onValueChange={(v) => update("party_size", v)}
              >
                <SelectTrigger
                  data-testid="reservation-party-trigger"
                  className="h-12 rounded-xl border-[#E8E2D6] bg-[#F9F6F0]/60 font-medium"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {PARTY_SIZES.map((p) => (
                    <SelectItem
                      key={p}
                      value={p}
                      data-testid={`reservation-party-${p}`}
                    >
                      {p} {p === "1" ? "guest" : "guests"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes (optional)" full>
              <textarea
                data-testid="reservation-notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                placeholder="Birthday? Allergies? Window seat? Let us know."
                className={`${inputClass} resize-none`}
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="reservation-submit-btn"
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D9534F] hover:bg-[#bf3f3b] disabled:opacity-60 text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:scale-[1.01]"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Sending..." : "Reserve my table"}
          </button>
          {done && (
            <p
              data-testid="reservation-success"
              className="mt-4 text-sm font-medium text-[#344E41]"
            >
              ✓ Got it — we&rsquo;ll be in touch shortly.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}

const inputClass =
  "w-full h-12 px-4 rounded-xl border border-[#E8E2D6] bg-[#F9F6F0]/60 font-medium text-[#2C2825] placeholder:text-[#6B635A]/70 focus:outline-none focus:border-[#D9534F] focus:bg-white transition-colors";

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-xs font-semibold tracking-wider uppercase text-[#6B635A] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
